/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import GridContainer from '../../components/Grid/GridContainer.jsx';
import GridItem from '../../components/Grid/GridItem.jsx';
import Button from '../../components/CustomButtons/ButtonExt';
import Card from '../../components/Card/Card.jsx';
import CardBody from '../../components/Card/CardBody.jsx';
import CardHeader from '../../components/Heading/CardHeaderExt';
import CardFooter from '../../components/Card/CardFooter.jsx';

import I18n from '../../components/Translate/I18n';

import { authActions, authApiActions, certificationActions } from '../../modules/auth/actions';

import { withStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import PasswordChangeModal from './PasswordChangeModal';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { Auth } from '../../modules/auth/Auth';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import CertificationRequestInterface, { CertificationRequestCollectionInterface } from '../../interfaces/platform/CertificationRequestInterface';
import CertificationRequest from './CertificationRequest';
import CustomInputPassword from '../../components/CustomInput/CustomInputPassword';
import InputAdornment from '@material-ui/core/InputAdornment';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import { Close } from 'mdi-material-ui';
import DiscardCertificationModal from './DiscardCertificationModal';
import { certificationRequestInt } from '../../helpers/CertificationRequestState';
import { DateHelperTs } from '../../helpers/DateHelperTs';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';

const CertificationPage = (props: { classes: any }): React.ReactElement => {
    const { classes } = props;
    const dispatch = useDispatch();
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [cardHidden, setCardHidden] = useState<boolean>(true);
    const [certificationRequest, setCertificationRequest] = useState<CertificationRequestInterface | null>(null);
    const [preLoadFinished, setPreLoadFinished] = useState<boolean>(false);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const loadRequests = useCallback((): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getUrl('/certification_requests', 'platform'),
            certificationActions.CERTIFICATION_REQUEST,
            [],
            [{ name: 'requestedAt', value: 'DESC' }],
            (requests: CertificationRequestCollectionInterface) => {
                if (requests && requests['hydra:member'] && requests['hydra:member'].length > 0) {
                    const tmpRequest = requests['hydra:member'][0];
                    setCertificationRequest(tmpRequest);
                }

                setPreLoadFinished(true);
            }
        );
    }, [dispatch, setCertificationRequest]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        if (name === 'password') {
            setPassword(value);
        }
    };

    useEffect(() => {
        setTimeout(() => {
            setCardHidden(false);
        }, 700);

        loadRequests();
    }, [loadRequests]);

    const generateRequest = (): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.postWithCallback(
            FormGeneralHelper.getUrl('/certification_requests', 'platform'),
            certificationActions.CERTIFICATION_REQUEST_POST,
            {
                platformUserId: Auth.getUserId(),
                requestedAt: DateHelperTs.getCurrentDateTime(),
                certificationPassword: password,
            },
            {
                onSuccess: () => {
                    loadRequests();
                },
                onError: (e: any) => {
                    const message = e.message?.message?.split('\n');
                    if (!message) {
                        return;
                    }

                    const output = (
                        <div>
                            {message.map((item: any, key: number) => {
                                return (
                                    <div key={'constraint-item-' + key}>
                                        {item}
                                        <br />
                                    </div>
                                );
                            })}
                        </div>
                    );

                    enqueueSnackbar(output, {
                        preventDuplicate: true,
                        key: new Date().getTime() + Math.random(),
                        variant: 'error',
                        action: (key) => (
                            <IconButton key="close" aria-label="close" color="inherit" onClick={(): void => closeSnackbar(key)}>
                                <Close />
                            </IconButton>
                        ),
                    });
                },
            }
        );
    };

    const handleDiscardCertificates = (): void => {
        if (Auth.isGenericUser()) {
            dispatch({ type: authActions.TOGGLE_MODAL, certificationModalOpen: true });
        }
    };

    return (
        <div className={classes.loginPageContainer}>
            <GridContainer justify="center">
                <GridItem xs={12} sm={6} md={6}>
                    <Card login className={cardHidden ? classes['cardHidden'] : ''}>
                        <CardHeader className={classes.textCenter} trans={'auth:login_page.' + process.env.REACT_APP_PANEL_NAME} />
                        <CardBody className={classes.textCenter}>
                            <h4>
                                <I18n trans={'auth:certificate.page_requires_a_valid_certificate'} />
                            </h4>

                            {preLoadFinished &&
                                UserRoleHelper.checkRole(roles.ROLE_PRE_CERTIFICATE_GET) &&
                                (!certificationRequest ||
                                    certificationRequest.state === certificationRequestInt.DISCARDED ||
                                    certificationRequest.state === certificationRequestInt.REJECTED ||
                                    certificationRequest.state === certificationRequestInt.EXPIRED) && (
                                    <>
                                        <CustomInputPassword
                                            labelText="auth:certificate.password"
                                            id="password"
                                            type="password"
                                            name="password"
                                            onChange={handleChange}
                                            formControlProps={{ fullWidth: true }}
                                            inputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Icon className={classes.inputAdornmentIcon}>lock_outline</Icon>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {!certificationRequest && (
                                            <CustomTextButtom
                                                callback={generateRequest}
                                                callbackParam={{}}
                                                color="primary"
                                                size="lg"
                                                label={'auth:certificate.generate_certificate_request'}
                                            />
                                        )}
                                        {certificationRequest && (
                                            <CustomTextButtom
                                                callback={generateRequest}
                                                callbackParam={{}}
                                                color="primary"
                                                size="lg"
                                                label={'auth:certificate.generate_certificate_request'}
                                            />
                                        )}
                                    </>
                                )}
                            {preLoadFinished && certificationRequest && (
                                <>
                                    <CertificationRequest request={certificationRequest} />
                                    {certificationRequest.state !== certificationRequestInt.DISCARDED &&
                                        certificationRequest.state !== certificationRequestInt.REJECTED &&
                                        certificationRequest.state !== certificationRequestInt.EXPIRED && (
                                            <>
                                                <CustomTextButtom
                                                    size={'lg'}
                                                    callbackParam={{}}
                                                    callback={handleDiscardCertificates}
                                                    label={'auth:certificate.discard_your_certificates'}
                                                />
                                            </>
                                        )}
                                </>
                            )}
                        </CardBody>
                        <CardFooter className={classes.justifyContentCenter}>
                            <CustomTextButtom
                                callback={(): void => {
                                    dispatch(authApiActions.certificationLogout());
                                }}
                                callbackParam={{}}
                                color="primary"
                                size="lg"
                                label={'auth:login_page.logout'}
                            />
                        </CardFooter>
                    </Card>
                </GridItem>
            </GridContainer>
            <PasswordChangeModal />
            <DiscardCertificationModal />
        </div>
    );
};

export default withStyles(oplAppStyle)(CertificationPage);
